/**
 * tf-pwa — Service Worker registration + PWA install prompt.
 *
 * Registra il SW solo in produzione (HTTPS). Espone helpers per
 * controllare update + trigger install prompt manualmente.
 *
 * @since v9.16 (Sprint 8 - PWA bootstrap)
 */

declare global {
  interface Window {
    tfPwa?: {
      register: () => Promise<ServiceWorkerRegistration | null>;
      unregister: () => Promise<boolean>;
      isInstalled: () => boolean;
      promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
      clearCache: () => Promise<void>;
    };
  }
}

let _swRegistration: ServiceWorkerRegistration | null = null;
let _deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const SW_PATH = '/sw.js';
const SW_SCOPE = '/';

/**
 * Registra il service worker. Idempotente.
 *
 * Solo in produzione + se browser supporta SW. Skipa in dev/localhost.
 */
export async function register(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined') return null;
  if (!('serviceWorker' in navigator)) return null;

  // Skip in dev locale (HTTP) o se PWA disabilitata via cookie debug
  if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
    console.info('[tf-pwa] SW skipped (no HTTPS)');
    return null;
  }
  if (document.cookie.includes('tf_pwa_disabled=1')) {
    console.info('[tf-pwa] SW skipped (debug cookie)');
    return null;
  }

  try {
    _swRegistration = await navigator.serviceWorker.register(SW_PATH, { scope: SW_SCOPE });
    console.info('[tf-pwa] Service Worker registered:', _swRegistration.scope);

    // Listen for updates
    _swRegistration.addEventListener('updatefound', () => {
      const newWorker = _swRegistration?.installing;
      if (!newWorker) return;
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // Update disponibile (skipWaiting già fatto da SW stesso)
          window.dispatchEvent(new CustomEvent('tf-pwa-update-available'));
        }
      });
    });

    return _swRegistration;
  } catch (e) {
    console.error('[tf-pwa] SW registration failed:', e);
    return null;
  }
}

/**
 * Unregister + cleanup cache. Utile per debug.
 */
export async function unregister(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  const registrations = await navigator.serviceWorker.getRegistrations();
  let success = false;
  for (const r of registrations) {
    success = (await r.unregister()) || success;
  }
  return success;
}

/**
 * Check se l'app è installata come PWA (modalità standalone).
 */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Trigger install prompt (richiede beforeinstallprompt event captured).
 * Ritorna outcome: 'accepted' | 'dismissed' | 'unavailable'.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!_deferredInstallPrompt) return 'unavailable';

  await _deferredInstallPrompt.prompt();
  const { outcome } = await _deferredInstallPrompt.userChoice;
  _deferredInstallPrompt = null;
  return outcome;
}

/**
 * Forza clear di tutta la cache (utile dopo update major).
 */
export async function clearCache(): Promise<void> {
  if (!_swRegistration?.active) return;
  _swRegistration.active.postMessage({ type: 'CLEAR_CACHE' });
}

// ============================================================
// Auto-bootstrap
// ============================================================

if (typeof window !== 'undefined') {
  // Capture install prompt for later
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _deferredInstallPrompt = e as BeforeInstallPromptEvent;
    window.dispatchEvent(new CustomEvent('tf-pwa-installable'));
  });

  // Auto-register dopo load (no-block initial render)
  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', () => register());
  }

  window.tfPwa = {
    register,
    unregister,
    isInstalled,
    promptInstall,
    clearCache,
  };
}
